import { LocaleContext } from "../contexts/LocaleContext";
import { useContext, useEffect, useRef, useState } from "react";
import logo from "../assets/images/logo_white.png";
import Notification from "./Notification";
import LogoutConfirmation from "./LogoutConfirmation";
import { useAuth } from "../hooks/useAuth";
import { useNotification } from "../hooks/useNotification";
import { useNavigate } from "react-router-dom";
import { dashboardService } from "../services/dashboardService";

const SEARCH_ROUTES = {
  trips:        (id) => `/trip-details/${id}`,
  providers:    (id) => `/provider-details/${id}`,
  customers:    (id) => `/customer-details/${id}`,
  bookings:     (id) => `/booking-details/${id}`,
  categories:   (id) => `/category-details/${id}`,
  cities:       (_id) => `/cities`,
  transactions: (_id) => `/transactions`,
};

const Header = () => {
  const { t, lang, changeLanguage } = useContext(LocaleContext);
  const { logout } = useAuth();
  const { unreadCount } = useNotification();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const searchCache = useRef({});
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showNotifications]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults(null);
      setShowResults(false);
      return;
    }
    if (searchCache.current[trimmed]) {
      setResults(searchCache.current[trimmed]);
      setShowResults(true);
      return;
    }
    const controller = new AbortController();
    const delay = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await dashboardService.searchAsync(trimmed, controller.signal);
        if (Object.values(res).some((arr) => arr?.length > 0)) searchCache.current[trimmed] = res;
        setResults(res);
        setShowResults(true);
      } catch {
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => {
      controller.abort();
      clearTimeout(delay);
    };
  }, [query]);

  const handleResultClick = (type, id) => {
    const route = SEARCH_ROUTES[type]?.(id);
    if (route) navigate(route);
    setQuery("");
    setShowResults(false);
  };

  const hasResults = results && Object.values(results).some((arr) => arr?.length > 0);

  return (
    <>
      <input type="checkbox" id="aside-toggle" className="aside-toggle" />
      <header>
        <nav>
          <ul>
            <li>
              <div className="brand-logo-box">
                <label htmlFor="aside-toggle" className="fa-solid fa-bars"></label>
                <img src={logo} alt="Logo" />
              </div>
            </li>
            <li ref={searchRef} style={{ position: "relative" }}>
              <div className="input-icon">
                <input
                  type="text"
                  className="form-control input-search"
                  placeholder={t("search")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => results && setShowResults(true)}
                />
                {searching
                  ? <span className="spinner-border spinner-border-sm text-secondary" style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }} />
                  : <i className="fas fa-search"></i>
                }
              </div>
              {showResults && (
                <div style={{
                  position: "absolute", top: "110%", left: 0, right: 0,
                  background: "#fff", borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  zIndex: 9999, maxHeight: 400, overflowY: "auto", minWidth: 320,
                }}>
                  {!hasResults ? (
                    <div style={{ padding: "12px 16px", color: "#888", fontSize: 14 }}>{t("noResultsFound")}</div>
                  ) : (
                    Object.entries(results).map(([type, items]) =>
                      items?.length > 0 && (
                        <div key={type}>
                          <div style={{ padding: "8px 16px 4px", fontSize: 11, fontWeight: 700, color: "#021641", textTransform: "uppercase", borderBottom: "1px solid #f0f0f0" }}>
                            {t(type)}
                          </div>
                          {items.map((item) => (
                            <div
                              key={item.id}
                              onClick={() => handleResultClick(type, item.id)}
                              style={{ padding: "10px 16px", cursor: "pointer", fontSize: 14, color: "#333", borderBottom: "1px solid #f9f9f9" }}
                              onMouseEnter={(e) => e.currentTarget.style.background = "#f5f7ff"}
                              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                            >
                              {item.displayName}
                            </div>
                          ))}
                        </div>
                      )
                    )
                  )}
                </div>
              )}
            </li>
          </ul>
          <ul className="header-icons">
            <li style={{ position: "relative" }}>
              <i
                className="fas fa-bell text-white cursor-pointer"
                onClick={() => setShowNotifications((prev) => !prev)}
              ></i>
              {unreadCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: "-12px",
                    right: "-5px",
                    background: "#e74c3c",
                    color: "#fff",
                    borderRadius: "50%",
                    fontSize: "10px",
                    fontWeight: "bold",
                    minWidth: "18px",
                    height: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 3px",
                    pointerEvents: "none",
                  }}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </li>
            <li>
              <div className="nav-logo-box">
                <img src={logo} alt="logo" />
              </div>
            </li>
            <li>
              <i
                className="fas fa-globe"
                title={lang === "en" ? t("switchLanguage") : t("switchLanguageToEnglish")}
                onClick={() => changeLanguage(lang === "en" ? "ar" : "en")}
              ></i>
            </li>
            <li>
              <i
                className="fas fa-power-off"
                title={t("logout")}
                data-bs-toggle="modal"
                data-bs-target="#logout-modal"
              ></i>
            </li>
          </ul>
        </nav>
      </header>
      {showNotifications && (
        <div ref={panelRef}>
          <Notification
            t={t}
            onClose={() => setShowNotifications(false)}
          />
        </div>
      )}
      <LogoutConfirmation t={t} handleLogout={logout} />
    </>
  );
};

export default Header;
